import type { FixtureEvent } from '@h2h/shared';
import { Info, SoccerBall } from '../../components/ui/icons';
import { cn } from '../../lib/cn';
import { eventKind, minuteLabel } from './helpers';

/** Goal-by-goal (and cards/subs) timeline for the live screen. */
export function MatchTimeline({ events, live }: { events: FixtureEvent[]; live: boolean }) {
  // Newest first feels right for a live feed; goals lead, other events follow.
  const ordered = [...events].sort((a, b) => b.elapsed + (b.extra ?? 0) - (a.elapsed + (a.extra ?? 0)));
  return (
    <>
      <div className="flex items-center justify-between pt-[22px]">
        <span className="text-[13px] font-bold tracking-snug text-white">Match timeline</span>
        {live && (
          <span className="inline-flex items-center gap-[5px] rounded-md bg-accent/[0.16] px-2 py-[3px]">
            <span className="h-[5px] w-[5px] animate-h2h-pulse rounded-full bg-accent" />
            <span className="text-[10px] font-extrabold uppercase tracking-eyebrow text-accent">Live</span>
          </span>
        )}
      </div>
      <div className="mt-3 flex flex-col gap-[2px] rounded-xl border border-white/[0.08] bg-white/[0.05] p-2">
        {ordered.map((e, i) => (
          <TimelineRow key={`${e.type}-${e.elapsed}-${e.extra ?? 0}-${i}`} e={e} />
        ))}
      </div>
    </>
  );
}

function TimelineRow({ e }: { e: FixtureEvent }) {
  const home = e.side === 'HOME';
  return (
    <div className="flex items-center gap-3 rounded-lg px-2 py-[9px]">
      <span className="num w-[34px] shrink-0 text-right text-[12px] font-extrabold text-white/55">{minuteLabel(e)}</span>
      <span className="grid h-[26px] w-[26px] shrink-0 place-items-center rounded-full bg-white/[0.07]">
        <EventIcon e={e} />
      </span>
      <div className={cn('flex-1', !home && 'text-right')}>
        <div className="text-[13px] font-bold tracking-snug text-white">
          {e.player ?? e.team}
          {e.type === 'GOAL' && e.detail !== 'Normal Goal' && (
            <span className="ml-[6px] text-[11px] font-semibold text-white/40">{e.detail.toLowerCase()}</span>
          )}
        </div>
        <div className="text-[11px] font-medium text-white/55">
          {e.player ? e.team : eventKind(e)}
          {e.assist ? ` · assist ${e.assist}` : ''}
        </div>
      </div>
      {e.type === 'GOAL' && e.scored && (
        <span className="num shrink-0 rounded-md bg-white/10 px-[9px] py-[3px] text-[12px] font-extrabold text-white">
          {e.homeGoals}–{e.awayGoals}
        </span>
      )}
    </div>
  );
}

function EventIcon({ e }: { e: FixtureEvent }) {
  if (e.type === 'GOAL') {
    return <SoccerBall width={15} height={15} className={e.scored ? 'text-white' : 'text-white/40'} />;
  }
  if (e.type === 'CARD') {
    const red = e.detail.toLowerCase().includes('red');
    return <span className={cn('h-[14px] w-[10px] rounded-[2px]', red ? 'bg-miss' : 'bg-[#F5C518]')} />;
  }
  if (e.type === 'SUBST') {
    return <span className="text-[12px] font-extrabold text-accent">⇄</span>;
  }
  return <Info width={14} height={14} className="text-white/55" />;
}
